'use client';

import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';
import { useTranslations } from 'next-intl';
import { css } from '@/styled-system/css';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { Module } from '@/src/components/features/property/ui/Module/Module';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import {
  moduleMetadata,
  type ModuleMetadata,
  type ModuleType,
} from '@/src/components/features/property/moduleRegistry';
import { AmenitiesModule } from '@/src/components/features/property/AmenitiesModule';
import { CompetitionModule } from '@/src/components/features/property/CompetitionModule';
import { FaqModule } from '@/src/components/features/property/FaqModule';
import { HousemateDetailsModule } from '@/src/components/features/property/HousemateDetailsModule';
import { InlineContactModule } from '@/src/components/features/property/InlineContactModule';
import { LegalInfoModule } from '@/src/components/features/property/LegalInfoModule';
import { ListingFeedbackModule } from '@/src/components/features/property/ListingFeedbackModule';
import { ListingSummaryModule } from '@/src/components/features/property/ListingSummaryModule';
import { NearbySchoolsModule } from '@/src/components/features/property/NearbySchoolsModule';
import { OpenHousesModule } from '@/src/components/features/property/OpenHousesModule';
import { PetsModule } from '@/src/components/features/property/PetsModule';
import { PricingComparisonModule } from '@/src/components/features/property/PricingComparisonModule';
import { ProviderListingsModule } from '@/src/components/features/property/ProviderListingsModule';
import { RecentlyViewedModule } from '@/src/components/features/property/RecentlyViewedModule';
import {
  MostPopularListingsModule,
  PetFriendlyListingsModule,
} from '@/src/components/features/property/RecommendationListingsModule';
import { SimilarListingsModule } from '@/src/components/features/property/SimilarListingsModule';
import { SourceInfoModule } from '@/src/components/features/property/SourceInfoModule';
import { TourConfirmationBanner } from '@/src/components/features/property/TourConfirmationModule';
import { PropertyMapModule } from '@/src/components/features/property/PropertyMapModule/PropertyMapModule';
import { UnitsModule } from '@/src/components/features/property/UnitsModule';

type StaticServerModuleType = 'about-building' | 'property-manager' | 'cost-and-fees';

export type ClientHdpModuleType = Exclude<ModuleType, StaticServerModuleType>;

type ClientModuleProps = {
  listing: PropertyDetailListing;
};

const NeighborhoodModule = dynamic<ClientModuleProps>(() =>
  import('@/src/components/features/property/NeighborhoodModule').then((mod) => mod.NeighborhoodModule),
);

// SSR cannot know the viewport, so mobile-only modules render into the HTML and
// unmount after hydration on desktop; hiding them at lg keeps that unmount from
// shifting painted layout.
const mobileOnlyModule = css({ lg: { display: 'none' } });

const clientModuleComponents: Record<ClientHdpModuleType, ComponentType<ClientModuleProps>> = {
  'listing-summary': ListingSummaryModule,
  'tour-confirmation': TourConfirmationBanner,
  'similar-listings': SimilarListingsModule,
  amenities: AmenitiesModule,
  units: UnitsModule,
  'property-map': PropertyMapModule,
  pets: PetsModule,
  neighborhood: NeighborhoodModule,
  'listing-feedback': ListingFeedbackModule,
  'nearby-schools': NearbySchoolsModule,
  'housemate-details': HousemateDetailsModule,
  competition: CompetitionModule,
  'pricing-comparison': PricingComparisonModule,
  'inline-contact': InlineContactModule,
  'recently-viewed': RecentlyViewedModule,
  'provider-listings': ProviderListingsModule,
  'most-popular-listings': MostPopularListingsModule,
  'pet-friendly-listings': PetFriendlyListingsModule,
  'open-houses': OpenHousesModule,
  'legal-info': LegalInfoModule,
  'source-info': SourceInfoModule,
  faq: FaqModule,
};

const clientModuleMetadata = moduleMetadata.filter(
  (module): module is ModuleMetadata & { type: ClientHdpModuleType } =>
    module.type !== 'about-building' && module.type !== 'property-manager' && module.type !== 'cost-and-fees',
);

export type ClientHdpInteractiveModulesProps = {
  listing: PropertyDetailListing;
  types: Array<ClientHdpModuleType>;
};

export function ClientHdpInteractiveModules({ listing, types }: ClientHdpInteractiveModulesProps) {
  const enabledTypes = new Set(types);
  const isDesktop = useIsDesktop();
  const t = useTranslations('hdp');

  return (
    <>
      {clientModuleMetadata
        .filter((module) => enabledTypes.has(module.type))
        .filter((module) => module.enabled !== false)
        .filter((module) => module.type !== 'neighborhood' || !isDesktop)
        .filter((module) => !module.shouldRender || module.shouldRender(listing))
        .map((module) => {
          const Component = clientModuleComponents[module.type];
          const title = module.titleKey ? t(module.titleKey) : module.title;

          if (module.componentOwnsChrome) {
            return <Component key={module.type} listing={listing} />;
          }

          return (
            <Module
              key={module.type}
              title={title}
              id={module.id}
              className={module.type === 'neighborhood' ? mobileOnlyModule : undefined}
            >
              <Component listing={listing} />
            </Module>
          );
        })}
    </>
  );
}
